/**
 * buildBeforeAfterComparison — flattens a Before + After photo into a
 * single side-by-side JPEG with labeled BEFORE / AFTER dividers, so
 * landlords opening a share link (or a printed PDF export) see the
 * comparison without having to drag any slider.
 *
 * We match heights (letterbox on the shorter one), draw a 4px black
 * divider with the labels on top, and export at 0.92 JPEG quality.
 * Returns a Blob suitable for FormData upload.
 */
export async function buildBeforeAfterComparison(beforeUrl, afterUrl, {
  labelBefore = "BEFORE",
  labelAfter = "AFTER",
  maxSide = 2400,          // cap to keep uploads reasonable
} = {}) {
  const load = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
  const [a, b] = await Promise.all([load(beforeUrl), load(afterUrl)]);

  // Normalize heights → letterbox the shorter one to match.
  const H = Math.min(maxSide, Math.max(a.naturalHeight, b.naturalHeight));
  const scale = (img) => H / img.naturalHeight;
  const aW = Math.round(a.naturalWidth * scale(a));
  const bW = Math.round(b.naturalWidth * scale(b));
  const dividerW = 4;
  const totalW = aW + bW + dividerW;

  const canvas = document.createElement("canvas");
  canvas.width = totalW;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  // Background fallback (in case the image has transparency)
  ctx.fillStyle = "#0F1F3A";
  ctx.fillRect(0, 0, totalW, H);

  ctx.drawImage(a, 0, 0, aW, H);
  ctx.drawImage(b, aW + dividerW, 0, bW, H);

  // Divider
  ctx.fillStyle = "#00C4CC";
  ctx.fillRect(aW, 0, dividerW, H);

  // Labels (top-left corner of each half)
  const fontPx = Math.max(18, Math.round(H * 0.028));
  ctx.font = `900 ${fontPx}px system-ui, -apple-system, sans-serif`;
  const padX = Math.round(H * 0.02);
  const padY = Math.round(H * 0.015);
  const drawLabel = (text, x) => {
    const metrics = ctx.measureText(text);
    const w = metrics.width + padX * 2;
    const h = fontPx + padY * 2;
    ctx.fillStyle = "rgba(15, 31, 58, 0.85)";
    ctx.fillRect(x, padY, w, h);
    ctx.fillStyle = "#00C4CC";
    ctx.textBaseline = "top";
    ctx.fillText(text, x + padX, padY + padY / 2);
  };
  drawLabel(labelBefore, padX);
  drawLabel(labelAfter, aW + dividerW + padX);

  return await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", 0.92));
}
